import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { lmError } from './lmApi';
import { CopyId, GhostButton, Pill, PrimaryButton } from './LmBits';
import { OwnerSelect } from './LmBulkDialog';
import { MATCH_LABELS, TAG_HELP, blockLabel, displayName, formatDate, labelOf, matchHelp, matchOf, statusHelp, statusOf } from './lmColumns';

/**
 * Row detail (#772 LM-04): source values beside staged values (source values
 * are immutable; staged values are editable only in Staging), match evidence
 * with candidates and an explicit decision, validation, owner and history.
 */
const EDITABLE_STATES = new Set(['needs_review', 'ready_for_pool', 'skipped']);

export default function LmRowDrawer({ open, rowId, tab, api, owners, fieldLabels, onClose, onChanged, canAssign }) {
    const [detail, setDetail] = useState(null);
    const [edits, setEdits] = useState({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    // P9 (#975): focus the drawer's content on open, not its first pill — the
    // first pill is a tooltip trigger, so Radix's default focus would open a
    // tooltip as the drawer appears.
    const contentRef = React.useRef(null);

    // `keepError`: the reload after an action must not wipe the error that action just set.
    const load = useCallback((keepError = false) => {
        if (!rowId) return;
        if (!keepError) setError(null);
        api.row(rowId).then((d) => { setDetail(d); setEdits({}); }).catch((e) => setError(lmError(e, 'Could not load this record.')));
    }, [api, rowId]);

    useEffect(() => { if (open) { setDetail(null); load(); } }, [open, load]);

    const row = detail?.row;
    const editable = row && EDITABLE_STATES.has(row.state);
    const fields = useMemo(() => {
        if (!row) return [];
        const keys = new Set([...Object.keys(row.staged_values || {}), ...Object.keys(row.mapped_values || {})]);
        ['pitchbook_id', 'pitchbook_person_id'].forEach((k) => keys.delete(k));
        return [...keys].sort((a, b) => labelOf(a, fieldLabels).localeCompare(labelOf(b, fieldLabels)));
    }, [row, fieldLabels]);

    const act = async (fn, success) => {
        setBusy(true); setError(null);
        try {
            const res = await fn();
            const failed = res?.results?.find?.((r) => !r.ok);
            if (failed) setError(failed.error?.message || blockLabel(failed.error?.code));
            else if (success) toast.success(success);
            load(true);
            onChanged?.();
        } catch (err) {
            setError(lmError(err, 'That change could not be saved.'));
        } finally {
            setBusy(false);
        }
    };

    const saveEdits = () => act(() => api.patchRow(rowId, edits), 'Staged values saved');
    const resolve = (target, decision, targetId) => act(
        () => api.bulk('resolve-match', { row_ids: [rowId], target, decision, target_id: targetId || null }), 'Match decision saved');
    const assign = (ownerId) => act(
        () => api.bulk('assign', { row_ids: [rowId], owner_id: ownerId === '__unassign__' ? null : ownerId }), 'Owner updated');

    const match = row?.match_full || {};
    const company = match.company;
    const errors = row?.validation_full?.errors || [];
    const warnings = row?.validation_full?.warnings || [];

    return (
        <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
            <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto p-0"
                          ref={contentRef} tabIndex={-1}
                          onOpenAutoFocus={(e) => { e.preventDefault(); contentRef.current?.focus(); }}
                          data-testid="lm-row-drawer">
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-border bg-muted/50 text-left">
                    <SheetTitle className="text-[17px] font-semibold tracking-[-0.015em] text-foreground">
                        {row ? displayName({ values: row.staged_values }) : 'Record'}
                    </SheetTitle>
                    <SheetDescription className="text-[12.5px] text-muted-foreground">
                        {row ? `${row.entity_type || 'Unresolved'} · row ${row.row_number} of ${detail?.import?.batch_name || 'import'}` : 'Loading…'}
                    </SheetDescription>
                    {row && (
                        <div className="flex flex-wrap gap-1.5 pt-1">
                            <Pill label={statusOf(row, tab)} description={statusHelp(row, tab)} testId="lm-badge-status" />
                            {(() => {
                                const m = matchHelp(row);
                                return <Pill label={matchOf(row)} description={m.description} detail={m.detail} testId="lm-badge-match" />;
                            })()}
                            {row.owner_name && <Pill label={row.owner_name} description={TAG_HELP.owner} />}
                        </div>
                    )}
                </SheetHeader>

                {!row && !error && <div className="p-6 flex items-center gap-2 text-[13px] text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>}
                {error && <div role="alert" className="mx-6 mt-4 px-3.5 py-2.5 rounded-[10px] bg-red-50 dark:bg-bad-bg text-[12.5px] text-red-800 dark:text-bad">{error}</div>}

                {row && (
                    <div className="px-6 py-5 space-y-6">
                        {(errors.length > 0 || warnings.length > 0) && (
                            <section aria-labelledby="lm-dr-val">
                                <h3 id="lm-dr-val" className="text-[13px] font-semibold text-foreground">Validation</h3>
                                <ul className="mt-2 space-y-1.5">
                                    {errors.map((e, i) => <li key={`e${i}`} className="px-3 py-2 rounded-lg bg-red-50 dark:bg-bad-bg text-[12.5px] text-red-800 dark:text-bad">{e.message}</li>)}
                                    {warnings.map((w, i) => <li key={`w${i}`} className="px-3 py-2 rounded-lg bg-amber-50 dark:bg-warn-bg text-[12.5px] text-amber-800 dark:text-warn">{w.message}</li>)}
                                </ul>
                            </section>
                        )}

                        <section aria-labelledby="lm-dr-match">
                            <h3 id="lm-dr-match" className="text-[13px] font-semibold text-foreground">Match</h3>
                            <p className="mt-1 text-[12.5px] text-ink-2">
                                {MATCH_LABELS[match.status] || 'Not matched yet'}
                                {match.evidence ? ` · evidence: ${String(match.evidence).replace(/_/g, ' ')}` : ''}
                                {row.match_decision && row.match_decision !== 'auto' ? ` · decision: ${row.match_decision}` : ''}
                            </p>
                            {(match.candidates || []).length > 0 && (
                                <ul className="mt-2 border border-border rounded-[10px] divide-y divide-border/60" data-testid="lm-dr-candidates">
                                    {match.candidates.map((cand) => (
                                        <li key={cand.id} className="flex items-center gap-3 px-3 py-2">
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[13px] font-medium text-foreground truncate">{cand.name}</div>
                                                <div className="text-xs text-muted-foreground truncate">{cand.detail || `id ${String(cand.id).slice(0, 8)}`} · {cand.evidence}</div>
                                            </div>
                                            {editable && (
                                                <GhostButton onClick={() => resolve('record', 'update', cand.id)} disabled={busy}
                                                             className="h-[30px]">{row.decision_target_id === cand.id ? 'Linked' : 'Link & update'}</GhostButton>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            )}
                            {editable && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                    <GhostButton onClick={() => resolve('record', 'new')} disabled={busy} className="h-[30px]">Create new record</GhostButton>
                                    <GhostButton onClick={() => resolve('record', 'skip')} disabled={busy} className="h-[30px]">Skip (do not import)</GhostButton>
                                </div>
                            )}
                            {company && (
                                <div className="mt-3 pt-3 border-t border-border/60">
                                    <div className="text-[12.5px] font-medium text-foreground">
                                        Company: {MATCH_LABELS[company.status] || company.status}
                                        {row.company_decision && row.company_decision !== 'auto' ? ` · ${row.company_decision === 'link' ? 'link to existing' : 'create new'}` : ''}
                                    </div>
                                    {editable && (company.candidates || []).map((cand) => (
                                        <div key={cand.id} className="flex items-center gap-2 mt-1.5">
                                            <span className="flex-1 text-[12.5px] text-ink-2 truncate">{cand.name} · {cand.detail || `id ${String(cand.id).slice(0, 8)}`}</span>
                                            <GhostButton onClick={() => resolve('company', 'link', cand.id)} disabled={busy} className="h-[28px]">Link company</GhostButton>
                                        </div>
                                    ))}
                                    {editable && company.status !== 'new' && (
                                        <GhostButton onClick={() => resolve('company', 'new')} disabled={busy} className="h-[28px] mt-1.5">Create a new company</GhostButton>
                                    )}
                                </div>
                            )}
                        </section>

                        <section aria-labelledby="lm-dr-values">
                            <h3 id="lm-dr-values" className="text-[13px] font-semibold text-foreground">Values</h3>
                            <p className="mt-0.5 text-xs text-muted-foreground">Source values are immutable. {editable ? 'Staged values are editable here.' : 'Move the record to Staging to edit it.'}</p>
                            <table className="mt-2 w-full border-collapse text-[12.5px]">
                                <thead>
                                    <tr className="text-left text-[11px] font-semibold tracking-[0.05em] text-muted-foreground">
                                        <th scope="col" className="py-1.5 pr-2">FIELD</th>
                                        <th scope="col" className="py-1.5 pr-2">IMPORTED</th>
                                        <th scope="col" className="py-1.5">STAGED</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {fields.map((k) => {
                                        const original = row.mapped_values?.[k] ?? '';
                                        const current = k in edits ? edits[k] : (row.staged_values?.[k] ?? '');
                                        const changed = String(original) !== String(row.staged_values?.[k] ?? '');
                                        return (
                                            <tr key={k} className="border-t border-border/60 align-top">
                                                <th scope="row" className="py-1.5 pr-2 font-medium text-ink-2 whitespace-nowrap">{labelOf(k, fieldLabels)}</th>
                                                <td className="py-1.5 pr-2 text-muted-foreground break-all">{String(original) || '—'}</td>
                                                <td className="py-1.5">
                                                    {editable ? (
                                                        <input value={current} onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value }))}
                                                               aria-label={`Staged ${labelOf(k, fieldLabels)}`}
                                                               className={`w-full h-[30px] px-2 border rounded-md bg-card text-foreground text-[12.5px] ${changed ? 'border-violet-600 dark:border-violet-400' : 'border-input'}`} />
                                                    ) : <span className="text-foreground break-all">{String(current) || '—'}</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                            {editable && Object.keys(edits).length > 0 && (
                                <div className="mt-2 flex justify-end gap-2">
                                    <GhostButton onClick={() => setEdits({})} disabled={busy}>Discard</GhostButton>
                                    <PrimaryButton onClick={saveEdits} disabled={busy} testId="lm-dr-save">Save changes</PrimaryButton>
                                </div>
                            )}
                            {(row.source_only || []).length > 0 && (
                                <div className="mt-3 text-xs text-muted-foreground">
                                    <span className="font-medium text-ink-2">Source-only (never written to the CRM): </span>
                                    {row.source_only.map((h) => `${h}: ${row.source_values?.[h] ?? ''}`).join(' · ')}
                                </div>
                            )}
                        </section>

                        {canAssign && !['committed', 'reverted', 'deleted', 'committing', 'imported', 'skipped'].includes(row.state) && (
                            <section aria-labelledby="lm-dr-owner">
                                <h3 id="lm-dr-owner" className="text-[13px] font-semibold text-foreground">Owner</h3>
                                <div className="mt-1 max-w-[320px]">
                                    <OwnerSelect owners={owners} value={row.owner_id || ''} includeUnassign id="lm-dr-owner-select"
                                                 onChange={(v) => v && assign(v)} />
                                </div>
                                <p className="mt-1 text-xs text-muted-foreground">An owner is required before Commit.</p>
                            </section>
                        )}

                        {row.commit_full && (
                            <section aria-labelledby="lm-dr-commit">
                                <h3 id="lm-dr-commit" className="text-[13px] font-semibold text-foreground">Commit</h3>
                                <p className="mt-1 text-[12.5px] text-ink-2">
                                    {row.commit_full.outcome ? `Outcome: ${row.commit_full.outcome}` : 'Not committed'}
                                    {row.commit_full.committed_at ? ` · ${formatDate(row.commit_full.committed_at, true)}` : ''}
                                </p>
                                {row.commit_full.error && (
                                    <p className="mt-1 text-[12.5px] text-red-800 dark:text-bad">{row.commit_full.error.message}</p>
                                )}
                            </section>
                        )}

                        <section aria-labelledby="lm-dr-history">
                            <h3 id="lm-dr-history" className="text-[13px] font-semibold text-foreground">History</h3>
                            <ol className="mt-2 space-y-1.5">
                                {(detail.events || []).map((e) => (
                                    <li key={e.event_id} className="text-xs text-ink-2">
                                        <span className="text-muted-foreground tabular-nums">{formatDate(e.created_at, true)}</span>{' · '}
                                        {e.action.replace(/^row\./, '').replace(/_/g, ' ')}
                                        {e.to_state ? ` → ${e.to_state.replace(/_/g, ' ')}` : ''}
                                        {e.actor_name ? ` · ${e.actor_name}` : ''}
                                        {e.reason ? ` · ${e.reason}` : ''}
                                    </li>
                                ))}
                            </ol>
                        </section>
                        <div className="space-y-1">
                            <CopyId label="Import ID" value={row.import_id} testId="lm-drawer-import-id" />
                            <div className="text-[11px] text-muted-foreground break-all">
                                Source {row.source_record_id} · snapshot {row.source_snapshot_id} · mapping {row.mapping_version} · {row.transform_version}
                            </div>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
